import { z } from 'zod';

// Order-related enums and types

// ─── Three-Axis Order State Schemas ─────────────────────────────────────────

/** Order State — business lifecycle */
export const OrderStateSchema = z.enum([
    'draft',
    'pendingAcceptance',
    'scheduled',
    'open',
    'held',
    'closed',
    'cancelled',
    'voided',
    'transferred',
    // Checkout-held (online order persisted before its charge). See the
    // OrderState union in @order-up/models for the contract.
    'awaitingPayment',
    'abandoned'
]);

/** Kitchen State — order-level production/fulfillment (derived from per-item statuses) */
export const KitchenStateSchema = z.enum([
    'none',
    'pending',
    'sent',
    'preparing',
    'ready',
    'served'
]);

/** Payment Status — financial state (enhanced with partiallyRefunded, voided) */
export const PaymentStatusSchema = z.enum([
    'unpaid',
    'processing',
    'partiallyPaid',
    'paid',
    'partiallyRefunded',
    'refunded',
    'voided',
    'failed',
    'comped'
]);

export const OrderTypeSchema = z.enum(['dineIn', 'takeOut', 'delivery', 'quickService']);

// Kitchen workflow enums (per-item status — includes 'voided')
export const KitchenStatusSchema = z.enum(['pending', 'sentToKitchen', 'inPreparation', 'ready', 'served', 'fired', 'cancelled', 'voided']);
export const KitchenSendPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);

// Sync states for offline-first operations
export const OrderSyncStateSchema = z.enum([
    'localOnly',       // Only exists locally
    'pendingSync',     // Needs to be synced
    'syncing',         // Currently syncing
    'synced',          // Successfully synced
    'syncFailed',      // Sync failed
    'conflicted'       // Sync conflict
]);

export const OrderSyncPrioritySchema = z.enum(['low', 'normal', 'high', 'critical']);

// Order source types - where the order originated from
export const OrderSourceSchema = z.enum(['pos', 'online', 'thirdParty', 'kiosk', 'phone', 'api', 'other']);

// External order source - specific third-party platform
export const ExternalOrderSourceSchema = z.enum([
  'ubereats',
  'doordash',
  'grubhub',
  'chownow',
  'foodhub',
  'gloriafood',
  'flipdish',
  'beyondmenu',
  'cloudwaitress',
  'eatstreet',
  'menufy',
  'kitchenhub',
  'square',
  'toast',
  'internal',
]);

// Check splitting
/** Split status of the order's checks */
export const OrderSplitStatusSchema = z.enum(['unsplit', 'split', 'partiallySplit', 'merged']);

// Discount and tax types
/** How a CATALOG discount is expressed. `comp` is never configured — see below. */
export const DiscountTypeSchema = z.enum(['percentage', 'fixedAmount']);
/**
 * How a discount ALREADY ON an order, a line or a check is expressed.
 *
 * Mirrors `AppliedDiscountType` in @order-up/models — read the comment there
 * for why `comp` is its own type and why `amountApplied`, never `value`, is
 * its money. `CheckDiscountSchema.discountType` already spelled this union out
 * inline; it now shares this one, so the two can never drift.
 */
export const AppliedDiscountTypeSchema = z.enum(['percentage', 'fixedAmount', 'comp']);
export const DiscountScopeSchema = z.enum(['order', 'item']);
export const TaxTypeSchema = z.enum(['percentage', 'fixed', 'none']);

// ─── Void Info Schemas ──────────────────────────────────────────────────────

/** Void information for order-level voids */
export const OrderVoidInfoSchema = z.object({
    reason: z.string().min(1),
    authorizedBy: z.string().min(1),
});

/** Void information for item-level voids */
export const OrderItemVoidInfoSchema = z.object({
    reason: z.string().min(1),
    authorizedBy: z.string().min(1),
    voidedAt: z.coerce.date(),
    foodCostIncurred: z.boolean(),
});

// Type exports
export type OrderState = z.infer<typeof OrderStateSchema>;
export type KitchenState = z.infer<typeof KitchenStateSchema>;
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;
export type OrderType = z.infer<typeof OrderTypeSchema>;
export type KitchenStatus = z.infer<typeof KitchenStatusSchema>;
export type KitchenSendPriority = z.infer<typeof KitchenSendPrioritySchema>;
export type OrderSyncState = z.infer<typeof OrderSyncStateSchema>;
export type OrderSyncPriority = z.infer<typeof OrderSyncPrioritySchema>;
export type OrderSource = z.infer<typeof OrderSourceSchema>;
export type ExternalOrderSource = z.infer<typeof ExternalOrderSourceSchema>;
export type DiscountType = z.infer<typeof DiscountTypeSchema>;
export type AppliedDiscountType = z.infer<typeof AppliedDiscountTypeSchema>;
export type DiscountScope = z.infer<typeof DiscountScopeSchema>;
export type TaxType = z.infer<typeof TaxTypeSchema>;
export type OrderSplitStatus = z.infer<typeof OrderSplitStatusSchema>;
